/* @flow */
import * as React from 'react';
import ConfirmDialog from 'components/ConfirmDialog';
import ToastActions from 'actions/ToastActions';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { withRouter } from 'app/context/Router';
import adminDistrictSchoolAdminsViewQuery from 'admin/queries/adminDistrictSchoolAdminsViewQuery.gql';
import adminOrgAdminsViewQuery from 'admin/queries/adminOrgAdminsViewQuery.gql';
import CurrentUser from 'auth/CurrentUser';

type Props = {
  router: *,
  firstName: string,
  name: string,
  onRequestHide: Function,
  organizationId: number,
  organizationType: string,
  uuid: string,
  districtId: ?number,
  removeAdmin: (organizationId: number, userUuid: string, districtId: ?number) => Promise<*>,
};

class AdminRemoveAdminDialog extends React.Component<Props> {
  removingSelf = CurrentUser.getUserUuid() === this.props.uuid;

  handleRemove = () => {
    const { organizationId, uuid, districtId } = this.props;

    this.props
      .removeAdmin(organizationId, uuid, districtId)
      .then(() => {
        if (this.removingSelf) {
          this.props.router.replace({ pathname: '/landing' });
        } else {
          ToastActions.showSuccessToast(`Admin successfully removed.`);
          this.props.onRequestHide();
        }
      })
      .catch(e => {
        ToastActions.showAPIError(e);
      });
  };

  render() {
    const { firstName, name, onRequestHide, organizationType } = this.props;

    const title = this.removingSelf
      ? 'Are you sure you want to remove yourself?'
      : `Are you sure you want to remove
          ${' '}
          ${name}?
        `;

    const text = this.removingSelf
      ? 'You will no longer have access to the Admin Console.'
      : `They will no longer be able to access the Admin Console. You can also remove
        ${' '}
        ${firstName}
        ${' '}
        from the
        ${' '}
        ${organizationType}.`;

    return (
      <ConfirmDialog
        actionButtonColor="apple"
        actionButtonText="Remove as admin"
        title={title}
        onConfirm={this.handleRemove}
        onRequestHide={onRequestHide}>
        {text}
      </ConfirmDialog>
    );
  }
}

const withRemoveAdmin = graphql(
  gql`
    mutation removeAdmin($input: AddRemoveAdminInput!) {
      removeAdmin(input: $input) {
        error {
          message
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      removeAdmin(organizationId: number, userUuid: string, districtId: ?number) {
        const input = { organizationId, userUuid };
        const applicableQuery = districtId
          ? { query: adminDistrictSchoolAdminsViewQuery, variables: { organizationId: districtId } }
          : {
              query: adminOrgAdminsViewQuery,
              variables: { organizationId },
            };
        return mutate({
          variables: { input },
          update: (
            store,
            {
              data: {
                removeAdmin: { error },
              },
            }
          ) => {
            if (error) {
              return;
            }

            const currentData = store.readQuery(applicableQuery);
            let currentAdmins;

            if (applicableQuery.query === adminDistrictSchoolAdminsViewQuery) {
              currentAdmins = currentData.organizations[0].childOrganizations.find(
                org => org.id === parseInt(organizationId, 10)
              ).admins;
            } else {
              currentAdmins = currentData.organizations[0].admins;
            }

            for (let i = 0; i < currentAdmins.length; i++) {
              if (currentAdmins[i].uuid === userUuid) {
                currentAdmins.splice(i, 1);
                break;
              }
            }
            store.writeQuery({ ...applicableQuery, data: currentData });
          },
        }).then(({ data: { removeAdmin } }) => {
          if (removeAdmin.error) {
            throw new Error(removeAdmin.error.message);
          }
          return Promise.resolve();
        });
      },
    }),
  }
);

export { AdminRemoveAdminDialog };
export default withRouter(withRemoveAdmin(AdminRemoveAdminDialog));
